import React, { useState, useRef, useEffect } from 'react';
import { handleActivationKey, handleEscapeKey } from '../utils/a11y.utils';
import useListboxDropdown from '../hooks/useListboxDropdown';

const FipDropdown = ({ fipList, selectedFip, onSelectFip, customDetails, labelId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getLastFourDigits = (maskedAccNumber) => {
    if (!maskedAccNumber) return '';
    return maskedAccNumber.slice(-4);
  };

  const getFipKey = (fip) =>
    `${fip.accountDetails.fiType}-${fip.accountDetails.accType}-${fip.accountDetails.maskedAccNumber}`;

  const otherFips =
    fipList?.filter(
      (fip) => selectedFip && getFipKey(fip) !== getFipKey(selectedFip)
    ) ?? [];

  const {
    listboxId,
    toggleId,
    close,
    open,
    handleToggleKeyDown,
    handleOptionKeyDown,
    setOptionRef,
  } = useListboxDropdown({
    isOpen,
    setIsOpen,
    optionCount: otherFips.length,
    onSelectIndex: (index) => handleSelectFip(otherFips[index]),
    label: 'Account list',
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleDocumentKeyDown = (event) => {
      handleEscapeKey(event, () => close());
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [close]);

  const toggleDropdown = () => {
    if (isOpen) close();
    else open();
  };

  const handleSelectFip = (fip) => {
    onSelectFip(fip);
    close();
  };

  const renderFipOption = (fip, { isToggle = false, optionIndex = null } = {}) => {
    const label = `${fip.fipName || 'Unknown Financial Institution'}, account ending ${getLastFourDigits(fip.accountDetails.maskedAccNumber)}`;

    return (
      <button
        type="button"
        key={getFipKey(fip)}
        id={isToggle ? toggleId : undefined}
        ref={optionIndex !== null ? (node) => setOptionRef(optionIndex, node) : undefined}
        role={isToggle ? undefined : 'option'}
        aria-selected={isToggle ? undefined : false}
        aria-label={label}
        className={`d-flex align-items-center p-2 w-100 card-action-btn ${!isToggle ? 'fip-option' : ''}`}
        style={{
          cursor: isToggle || !isToggle ? 'pointer' : 'default',
          borderBottom: !isToggle ? '1px solid #f0f0f0' : 'none',
          backgroundColor: isToggle ? 'transparent' : 'white',
          minHeight: '40px',
          borderRadius: isToggle ? '20px' : 0,
        }}
        aria-expanded={isToggle && fipList.length > 1 ? isOpen : undefined}
        aria-haspopup={isToggle && fipList.length > 1 ? 'listbox' : undefined}
        aria-controls={isToggle && fipList.length > 1 ? listboxId : undefined}
        aria-labelledby={isToggle && labelId ? labelId : undefined}
        disabled={isToggle && fipList.length <= 1}
        onClick={() => {
          if (isToggle) toggleDropdown();
          else handleSelectFip(fip);
        }}
        onKeyDown={(event) => {
          if (isToggle) {
            handleToggleKeyDown(event);
            handleActivationKey(event, toggleDropdown);
            return;
          }
          if (optionIndex !== null) {
            handleOptionKeyDown(event, optionIndex);
          }
        }}
      >
        <div className="flex-shrink-0" style={{ width: '24px', height: '24px', marginLeft:'5px' ,marginBottom:'2.5px', marginRight: '8px'}}>
          {fip.fipLogoUri ? (
            <img
              src={fip.fipLogoUri}
              alt=""
              aria-hidden="true"
              className="w-100 h-100 rounded"
              style={{ objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}

          <div
            className={`w-100 h-100 bg-light border rounded d-${fip.fipLogoUri ? 'none' : 'flex'} align-items-center justify-content-center`}
            style={{
              fontSize: '10px',
              fontWeight: '600',
              color: customDetails?.primaryBtnColor
            }}
            aria-hidden="true"
          >
            {fip.fipName?.charAt(0)?.toUpperCase() || 'FIP'}
          </div>
        </div>

        <div className="flex-grow-1">
          <div style={{ fontSize: '12px', color : customDetails?.primaryBtnColor, textAlign: 'left'}}>
            <span className="fw-600" >{fip.fipName || 'Unknown Financial Institution'} •</span>
            <span className="ms-2">{getLastFourDigits(fip.accountDetails.maskedAccNumber)}</span>
          </div>
        </div>

        {isToggle && fipList.length > 1 && (
          <div className="ms-2" aria-hidden="true">
            <div
              style={{
                width: '12px',
                height: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            >
              <div
                style={{
                  width: '4px',
                  height: '4px',
                  borderRight: `1px solid ${customDetails?.primaryBtnColor}`,
                  borderBottom: `1px solid ${customDetails?.primaryBtnColor}`,
                  transform: 'rotate(45deg)',
                  marginTop: '-1px',
                }}
              />
            </div>
          </div>
        )}
      </button>
    );
  };

  if (!fipList || fipList.length === 0) {
    return null;
  }

  const currentFip = fipList.length === 1 ? fipList[0] : selectedFip;

  return (
    <div
      className="mb-3 position-relative fip-dropdown-container"
      ref={dropdownRef}
      style={{
        backgroundColor: "#FFFFFF",
        borderRadius: '20px',
        border: `1px solid ${customDetails?.primaryBtnColor}`
      }}
    >
      {renderFipOption(currentFip, { isToggle: true })}

      {fipList.length > 1 && isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Select account"
          className="position-absolute w-100 bg-white border rounded shadow mt-1"
          style={{
            top: '100%',
            left: '0',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {otherFips.map((fip, index) => renderFipOption(fip, { optionIndex: index }))}
        </div>
      )}

      <style jsx>{`
        .fip-option:hover {
          background-color: var(--bs-light) !important;
        }
        .fip-dropdown-container {
          width: 50%;
          border-radius: 3rem;
        }
        @media (max-width: 600px) {
          .fip-dropdown-container {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default FipDropdown;
